import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { resolveMember } from '../utils/resolveMember';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import Layout from '../components/Layout';
import AdultNextSteps from '../components/AdultNextSteps';
import {
  Box, Typography, Paper, Button, CircularProgress, Stack, Avatar, Chip, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';

export default function Welcome() {
  const navigate = useNavigate();
  const location = useLocation();
  const previewMode = new URLSearchParams(location.search).get('preview') === '1';
  const [userData, setUserData] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Preview mode: synthesize fake user/athlete from sessionStorage form data
      if (previewMode) {
        try {
          const formStr = sessionStorage.getItem('previewFormData');
          const form = formStr ? JSON.parse(formStr) : {};
          if (cancelled) return;
          setUserData({
            firstName: form.firstName || 'Test',
            lastName: form.lastName || 'Parent',
            email: 'preview@example.com',
          });
          const fakeAthletes = [];
          if (form.accountType === 'junior') {
            fakeAthletes.push({
              id: 'preview-1',
              firstName: form.athleteFirstName || 'Test',
              lastName: form.athleteLastName || 'Athlete',
              athleteType: 'junior',
              skillGroup: form.beachExperience || 'Beginner',
            });
          } else if (form.accountType === 'adult') {
            fakeAthletes.push({
              id: 'preview-1',
              firstName: form.firstName || 'Test',
              lastName: form.lastName || 'Adult',
              athleteType: 'adult',
              skillGroup: form.beachExperience || 'Beginner',
            });
          }
          setAthletes(fakeAthletes);
          setLoading(false);
          return;
        } catch (e) {
          console.error('Preview load error:', e);
        }
      }

      // Real mode: load from Firestore
      const user = auth.currentUser;
      if (!user) { navigate('/'); return; }
      try {
        // Canonical: a merged login's own doc is a pointer with no profile on it.
        const { data: canon } = await resolveMember(db, user);
        if (cancelled) return;
        const u = canon || {};
        setUserData(u);

        const found = [];
        const byParent = await getDocs(query(collection(db, 'athletes'), where('parentUserId', '==', user.uid)));
        byParent.docs.forEach(d => found.push({ id: d.id, ...d.data() }));
        const byUserId = await getDocs(query(collection(db, 'athletes'), where('userId', '==', user.uid)));
        byUserId.docs.forEach(d => {
          if (!found.some(a => a.id === d.id)) found.push({ id: d.id, ...d.data() });
        });
        if (!cancelled) setAthletes(found);
      } catch (e) {
        console.error('Welcome load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, previewMode]);

  if (loading) {
    return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>;
  }

  const hasJuniors = athletes.some(a => a.athleteType === 'junior');
  const isAdult = athletes.some(a => a.athleteType === 'adult');

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720, mx: 'auto' }}>

        {previewMode && (
          <Alert severity="warning" icon={false}
                 sx={{ mb: 2, bgcolor: '#C9A84C', color: '#0D1E35', border: '2px solid #fff', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 1 }}>🔍 PREVIEW MODE</Typography>
                <Typography variant="caption">No real account or athlete created. Buttons go to live pages — be careful.</Typography>
              </Box>
              <Button size="small" variant="contained"
                      onClick={() => navigate('/tournament-admin')}
                      sx={{ bgcolor: '#0D1E35', color: '#fff', '&:hover': { bgcolor: '#1a3a5c' } }}>
                Exit
              </Button>
            </Box>
          </Alert>
        )}

        {/* Hero */}
        <Paper elevation={2} sx={{
          p: 4, borderRadius: 3, mb: 3, textAlign: 'center',
          background: 'linear-gradient(135deg, #0D1E35 0%, #1E3A5F 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#27AE60' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#C9A84C', mb: 1 }}>
            Welcome to Torch!
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
            {userData?.firstName ? `Hi ${userData.firstName}, your` : 'Your'} account is all set up.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            We've sent a confirmation email to {userData?.email}.
          </Typography>
        </Paper>

        {/* Athletes registered */}
        {athletes.length > 0 && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>
              {hasJuniors ? 'Athlete' : 'Player'}{athletes.length > 1 ? 's' : ''} Registered
            </Typography>
            <Stack spacing={1}>
              {athletes.map(a => (
                <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                  <Avatar sx={{ backgroundColor: '#C9A84C', width: 36, height: 36, fontSize: '0.9rem' }}>
                    {(a.firstName?.[0] || '') + (a.lastName?.[0] || '')}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.firstName} {a.lastName}</Typography>
                    {a.skillGroup && <Chip label={a.skillGroup} size="small" sx={{ fontSize: '0.65rem', height: 18, mr: 0.5 }} />}
                    {a.gradYear && <Typography variant="caption" color="text.secondary">Class of {a.gradYear}</Typography>}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* An adult says what they came for, so the registration gets a
            follow-up. Not in preview: answering would text the club. */}
        {isAdult && !hasJuniors && !previewMode && <AdultNextSteps />}

        {/* Next steps */}
        <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>
            Next Steps
          </Typography>

          {/* Step 1: Training Plans */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1.5, backgroundColor: '#FFF8E1', borderRadius: 2, border: '2px solid #C9A84C' }}>
            <ShoppingCartIcon sx={{ color: '#C9A84C', fontSize: 32 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>1. Training Plans</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                {hasJuniors
                  ? 'Get a session pack so your athlete can attend practices.'
                  : isAdult
                  ? 'Get a session pack so you can attend practices.'
                  : 'Get a session pack to attend practices.'}
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => {
              if (previewMode) {
                const previewType = athletes[0]?.athleteType || 'adult';
                navigate(`/training-plans?preview=${previewType}`);
              } else {
                navigate('/training-plans');
              }
            }}
                    sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2c4a7c' } }}>
              Buy Now
            </Button>
          </Box>

          {/* Step 2: View schedule */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1.5, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
            <CalendarMonthIcon sx={{ color: '#1E3A5F', fontSize: 32 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>2. View the Schedule</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>See upcoming classes and pick which ones to attend.</Typography>
            </Box>
            <Button variant="outlined" onClick={() => navigate('/schedule')}
                    sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>
              View
            </Button>
          </Box>

          {/* Step 3: Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
            <PersonIcon sx={{ color: '#1E3A5F', fontSize: 32 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>3. Review Your Account</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Add a photo, edit details, or update notification preferences.</Typography>
            </Box>
            <Button variant="outlined" onClick={() => navigate('/my-account')}
                    sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>
              Open
            </Button>
          </Box>
        </Paper>

        {/* Skip */}
        <Box sx={{ textAlign: 'center' }}>
          <Button variant="text" startIcon={<HomeIcon />} onClick={() => navigate('/')}
                  sx={{ color: '#888' }}>
            Skip to Home
          </Button>
        </Box>

      </Box>
    </Layout>
  );
}
